// presets.js — NFL-accurate preset generators (side-aware)
// All depths in yards; convert with yds() in caller.

// FIELD CONFIG
// We assume coordinates passed in are in YARDS relative to field (or will be scaled).
// But existing app uses 0..100 %.
// We will export a helper to allow the App to pass % and get % back.

export const buildSideHelpers = (FIELD_W) => ({
    isLeft: (x) => x < FIELD_W / 2,
    sidelineX: (x) => (x < FIELD_W / 2 ? 2 : FIELD_W - 2), // small inset from edge (yards)
});

// Helper to scale points relative to base
export const scaleRoutePoints = (points, base, depthScale = 1, widthScale = 1) => {
    return points.map(pt => {
        const dx = pt.x - base.x;
        const dy = pt.y - base.y;
        return { x: base.x + dx * widthScale, y: base.y + dy * depthScale };
    });
};

export const makeWR = (yds, FIELD_W) => {
    const { isLeft, sidelineX } = buildSideHelpers(FIELD_W);
    // returns absolute points (array of {x,y}) given {x,y} base
    return {
        // QUICK
        Hitch_5: (b) => [{ x: b.x, y: b.y - yds(5) }],
        QuickOut_3: (b) => [{ x: b.x, y: b.y - yds(3) }, { x: sidelineX(b.x), y: b.y - yds(3) }],
        Slant_3: (b) => [{ x: b.x, y: b.y - yds(3) }, { x: b.x + (isLeft(b.x) ? yds(5) : -yds(5)), y: b.y - yds(6) }],
        Stick_5: (b) => [{ x: b.x, y: b.y - yds(5) }, { x: b.x + (isLeft(b.x) ? yds(2) : -yds(2)), y: b.y - yds(5) }],

        // INTERMEDIATE
        Out_10: (b) => [{ x: b.x, y: b.y - yds(10) }, { x: sidelineX(b.x), y: b.y - yds(10) }],
        Dig_10: (b) => [{ x: b.x, y: b.y - yds(10) }, { x: b.x + (isLeft(b.x) ? yds(10) : -yds(10)), y: b.y - yds(10) }],
        Curl_10: (b) => [{ x: b.x, y: b.y - yds(12) }, { x: b.x, y: b.y - yds(10) }],
        Comeback_12: (b) => [{ x: b.x, y: b.y - yds(15) }, { x: b.x + (isLeft(b.x) ? yds(4) : -yds(4)), y: b.y - yds(12) }],
        Corner_12: (b) => [{ x: b.x, y: b.y - yds(10) }, { x: b.x + (isLeft(b.x) ? -yds(12) : yds(12)), y: b.y - yds(22) }], // Outward
        Post_12: (b) => [{ x: b.x, y: b.y - yds(10) }, { x: b.x + (isLeft(b.x) ? yds(12) : -yds(12)), y: b.y - yds(22) }],  // Inward

        // VERTICAL
        Go: (b) => [{ x: b.x, y: b.y - yds(25) }],
        Fade: (b) => [{ x: (b.x + sidelineX(b.x)) / 2, y: b.y - yds(10) }, { x: sidelineX(b.x), y: b.y - yds(20) }],
        Seam: (b) => [{ x: b.x, y: b.y - yds(20) }],

        // SCREENS
        Bubble: (b) => [{ x: b.x + (isLeft(b.x) ? -yds(4) : yds(4)), y: b.y + yds(1) }, { x: b.x + (isLeft(b.x) ? -yds(10) : yds(10)), y: b.y + yds(1) }],
        NowScreen: (b) => [{ x: b.x, y: b.y }], // Just stand there
        Tunnel: (b) => [{ x: b.x, y: b.y - yds(2) }, { x: b.x + (isLeft(b.x) ? yds(5) : -yds(5)), y: b.y + yds(1) }],

        // DOUBLE MOVES
        Sluggo: (b) => [
            { x: b.x, y: b.y - yds(3) },
            { x: b.x + (isLeft(b.x) ? yds(3) : -yds(3)), y: b.y - yds(5) }, // Slant
            { x: b.x + (isLeft(b.x) ? yds(3) : -yds(3)), y: b.y - yds(20) } // Go
        ],
        OutAndUp: (b) => [
            { x: b.x, y: b.y - yds(8) },
            { x: sidelineX(b.x) + (isLeft(b.x) ? yds(2) : -yds(2)), y: b.y - yds(8) }, // Out fake
            { x: sidelineX(b.x) + (isLeft(b.x) ? yds(2) : -yds(2)), y: b.y - yds(20) } // Up
        ],
        HitchAndGo: (b) => [
            { x: b.x, y: b.y - yds(6) },
            { x: b.x, y: b.y - yds(4) }, // Hitch
            { x: b.x, y: b.y - yds(20) } // Go
        ],
        PostCorner: (b) => [
            { x: b.x, y: b.y - yds(10) },
            { x: b.x + (isLeft(b.x) ? yds(4) : -yds(4)), y: b.y - yds(14) }, // Post
            { x: b.x + (isLeft(b.x) ? -yds(6) : yds(6)), y: b.y - yds(22) } // Corner
        ],
        CornerPost: (b) => [
            { x: b.x, y: b.y - yds(10) },
            { x: b.x + (isLeft(b.x) ? -yds(4) : yds(4)), y: b.y - yds(14) }, // Corner
            { x: b.x + (isLeft(b.x) ? yds(6) : -yds(6)), y: b.y - yds(22) } // Post
        ]
    };
};

export const makeRBPass = (yds, FIELD_W) => {
    const { isLeft, sidelineX } = buildSideHelpers(FIELD_W);
    return {
        Flat: (b) => [{ x: sidelineX(b.x), y: b.y - yds(0.5) }],
        Swing: (b) => [
            { x: b.x + (isLeft(b.x) ? -yds(6) : yds(6)), y: b.y + yds(1) },
            { x: b.x + (isLeft(b.x) ? -yds(12) : yds(12)), y: b.y - yds(2) },
        ],
        Angle_Texas: (b) => [
            { x: b.x, y: b.y - yds(3) },
            { x: b.x + (isLeft(b.x) ? -yds(6) : yds(6)), y: b.y - yds(8) },
        ],
        Wheel: (b) => [
            { x: b.x + (isLeft(b.x) ? -yds(6) : yds(6)), y: b.y },
            { x: sidelineX(b.x), y: b.y - yds(15) },
        ],
    };
};

export const makeRBRush = (yds) => ({
    Dive: (b) => [{ x: b.x, y: b.y - yds(5) }],
    OffTackle_Left: (b) => [{ x: b.x - yds(4), y: b.y - yds(6) }],
    OffTackle_Right: (b) => [{ x: b.x + yds(4), y: b.y - yds(6) }],
    Sweep_Left: (b) => [{ x: b.x - yds(10), y: b.y - yds(4) }],
    Sweep_Right: (b) => [{ x: b.x + yds(10), y: b.y - yds(4) }],
});

export const makeQB = (yds) => ({
    Drop3: (b) => [{ x: b.x, y: b.y + yds(3) }],
    Drop5: (b) => [{ x: b.x, y: b.y + yds(5) }],
    Drop7: (b) => [{ x: b.x, y: b.y + yds(7) }],
    Roll_Right: (b) => [{ x: b.x + yds(8), y: b.y + yds(4) }],
    Roll_Left: (b) => [{ x: b.x - yds(8), y: b.y + yds(4) }],
});

export const makeBlocking = (yds) => ({
    PassSet: (b) => [{ x: b.x, y: b.y + yds(1.5) }],
    Run_Up: (b) => [{ x: b.x, y: b.y - yds(2.5) }],
    Run_Left: (b) => [{ x: b.x - yds(2.5), y: b.y - yds(1.5) }],
    Run_Right: (b) => [{ x: b.x + yds(2.5), y: b.y - yds(1.5) }],
});

export const makeDefense = (yds, FIELD_W) => {
    const { isLeft } = buildSideHelpers(FIELD_W);
    return {
        Backpedal8: (b) => [{ x: b.x, y: b.y - yds(8) }],
        CurlFlat: (b) => [
            { x: b.x + (isLeft(b.x) ? -yds(10) : yds(10)), y: b.y - yds(10) },
        ],
        Hook10: (b) => [{ x: b.x, y: b.y - yds(10) }],
        Seam15: (b) => [{ x: b.x, y: b.y - yds(15) }],
        DeepThird: (b) => [
            { x: b.x + (isLeft(b.x) ? -yds(12) : yds(12)), y: b.y - yds(18) },
        ],
        DeepHalf: (b) => [{ x: b.x, y: b.y - yds(18) }],
        Blitz8: (b) => [{ x: b.x, y: b.y + yds(8) }], // toward offense (down)
        ContainEdge: (b) => [
            { x: b.x + (isLeft(b.x) ? -yds(6) : yds(6)), y: b.y + yds(4) },
        ],
        SpyQB: (b) => [{ x: b.x, y: b.y - yds(2) }],
    };
};

export const makeZones = (profileConfig) => {
    // defaults for Adult/Pro (1.0)
    const base = {
        'Flat (Blue)': { w: 12, h: 7, color: '#60a5fa' },
        'Hook (Yellow)': { w: 14, h: 9, color: '#fbbf24' },
        'Deep Half (Purple)': { w: 20, h: 14, color: '#a78bfa' },
        'Deep Third (Green)': { w: 16, h: 16, color: '#34d399' }
    };

    // Scale zone sizes by routeWidthScale (proxy for age/field size?) 
    const scale = profileConfig?.routeWidthScale || 1.0;

    const scaled = {};
    Object.entries(base).forEach(([k, z]) => {
        scaled[k] = { ...z, w: z.w * scale, h: z.h * scale };
    });
    return scaled;
};

export const getOrGeneratePresets = (player, profileConfig = null) => {
    const W_YDS = 53.3;
    const H_YDS = 100.0;

    const baseInYards = {
        x: player.x * (W_YDS / 100),
        y: player.y * (H_YDS / 100)
    };

    const ydsIdentity = (v) => v;

    const qb = makeQB(ydsIdentity);
    const wr = makeWR(ydsIdentity, W_YDS);
    const rbPass = makeRBPass(ydsIdentity, W_YDS);
    const rbRush = makeRBRush(ydsIdentity);
    const block = makeBlocking(ydsIdentity);
    const def = makeDefense(ydsIdentity, W_YDS);
    const zones = makeZones(profileConfig);

    // Scaling Factors from profile
    const dScale = profileConfig?.routeDepthScale || 1.0;
    const wScale = profileConfig?.routeWidthScale || 1.0;

    // convert dicts back to % AND Apply Scale
    const process = (ptArr) => {
        // 1. Scale relative to origin
        const scaled = scaleRoutePoints(ptArr, baseInYards, dScale, wScale);
        // 2. Convert to %
        return scaled.map(p => ({
            x: (p.x / W_YDS) * 100,
            y: (p.y / H_YDS) * 100
        }));
    };

    const mapObj = (obj) => Object.fromEntries(Object.entries(obj).map(([k, fn]) => [
        k,
        () => process(fn(baseInYards))
    ]));

    return {
        'QB (dropbacks / rollouts)': mapObj(qb),
        'WR / TE Routes': mapObj(wr),
        'RB/FB Routes': { ...mapObj(rbPass), ...mapObj(rbRush) },
        'Blocking': mapObj(block),
        'Defense (drops / blitz)': mapObj(def),
        'Zones (defense bubble)': zones
    };
};
